"use client";

import { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { HeaderEdit } from "../HeaderSection/HeaderEdit";
import { AboutEdit } from "../AboutSection/AboutEdit";
import EducationEdit from "../EducationSection/EducationEdit";
import { CertificatesEdit } from "../CertificatesSection/CertificatesEdit";
import { SubjectsEdit } from "../SubjectsSection/SubjectsEdit";
import { LanguagesEdit } from "../LanguagesSection/LanguagesEdit";
import { SettingsEdit } from "../settings/SettingsEdit";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import { PersistentFooterActions } from "./PersistentFooterActions";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore.tsx";

export default function ProfileEditDialog() {
  const { profileData } = useProfileDataStore();
  const { isOpen, activeTab, close, setTab } = useProfileEditDialogStore();
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const clearFooter = useProfileEditFooterStore((s) => s.clearFooter);

  const tabs = useMemo(
    () => [
      { id: "header", label: t("profile:headerSection.edit"), icon: "mdi:account-edit-outline" },
      { id: "about", label: t("profile:aboutSectionEdit.about"), icon: "mdi:about-circle-outline" },
      { id: "education", label: t("profile:educationSection.edit"), icon: "mdi:school-outline" },
      {
        id: "certificates",
        label: t("profile:certificatesSection.edit"),
        icon: "mdi:certificate-outline",
      },
      { id: "subjects", label: t("profile:subjectsSection.edit"), icon: "mdi:book-outline" },
      { id: "languages", label: t("profile:languagesSection.edit"), icon: "mdi:language" },
      { id: "settings", label: t("profile:settingsSection.edit"), icon: "mdi:cog-outline" },
    ],
    [t],
  );

  const selectedTab = useMemo(
    () => tabs.find((tItem) => tItem.id === activeTab),
    [tabs, activeTab],
  );

  // Clear footer for tabs that do not provide a persistent footer
  useEffect(() => {
    if (activeTab === "header" || activeTab === "about" || activeTab === "settings") {
      clearFooter();
    }
  }, [activeTab, clearFooter]);

  const contentWrapper = (content: JSX.Element, sectionLabel?: string) => (
    <ScrollArea className="h-[calc(100dvh_-_2rem)] max-h-full">
      <div className="container mx-auto h-full w-full px-6 pt-6 pb-24 max-sm:px-3">
        {sectionLabel && (
          <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
            {sectionLabel}
          </div>
        )}
        {content}
      </div>
    </ScrollArea>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className={
          "bg-background/40 text-accent-foreground h-dvh w-dvw max-w-none rounded-none border-none p-0 shadow-2xl backdrop-blur-3xl lg:h-[calc(100dvh_-_2rem)] lg:w-[calc(100dvw_-_2rem)]"
        }
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
          <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
        </div>

        <div className="relative flex h-full">
          {/* Side navigation - desktop */}
          <div className="bg-background/75 hidden h-full w-72 flex-shrink-0 flex-col backdrop-blur-3xl xl:flex">
            <div className="px-5 py-6">
              <h2 className="text-accent-foreground text-xl font-semibold">
                {t("profile:editDialog.title", { defaultValue: "Profile settings" })}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("profile:editDialog.subtitle", {
                  defaultValue: "Manage your profile details across sections",
                })}
              </p>
            </div>
            <ScrollArea className="h-full">
              <div className="space-y-1 px-3 pb-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`group flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? "bg-accent/20 text-accent-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-accent/10"
                    }`}
                    onClick={() => setTab(tab.id as any)}
                  >
                    <Icon height="1.125rem" icon={tab.icon} width="1.125rem" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Main Content - Tabs for mobile and content area */}
          <div className="flex-1">
            {/* Mobile section selector */}
            <div className="px-3 pt-12 xl:hidden">
              <Select value={activeTab} onValueChange={(v) => setTab(v as any)}>
                <SelectTrigger className="flex w-full">
                  <SelectValue className="truncate overflow-hidden">
                    <span className="flex max-w-full items-center gap-2 truncate">
                      {selectedTab ? (
                        <>
                          <Icon height="1.125rem" icon={selectedTab.icon} width="1.125rem" />
                          <span className="truncate">{selectedTab.label}</span>
                        </>
                      ) : null}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {tabs.map((tItem) => (
                    <SelectItem key={tItem.id} value={tItem.id}>
                      <div className="flex items-center gap-2">
                        <Icon height="1.125rem" icon={tItem.icon} width="1.125rem" />
                        <span className="text-sm">{tItem.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content area */}
            <div className="relative h-full">
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="h-full"
                >
                  {activeTab === "header" &&
                    contentWrapper(
                      <HeaderEdit onSave={close} />,
                      t("profile:headerSection.edit", { defaultValue: "Header" }),
                    )}
                  {activeTab === "about" &&
                    contentWrapper(
                      <AboutEdit onSave={close} />,
                      t("profile:aboutSectionEdit.about", { defaultValue: "About" }),
                    )}
                  {activeTab === "education" &&
                    contentWrapper(
                      <EducationEdit onSave={close} />,
                      t("profile:educationSection.educationHistory", { defaultValue: "Education" }),
                    )}
                  {activeTab === "certificates" &&
                    contentWrapper(
                      <CertificatesEdit onSave={close} />,
                      t("profile:certificatesSection.edit", { defaultValue: "Certificates" }),
                    )}
                  {activeTab === "subjects" &&
                    contentWrapper(
                      <SubjectsEdit onCancel={close} onSave={close} />,
                      t("profile:subjectsSection.edit", { defaultValue: "Subjects" }),
                    )}
                  {activeTab === "languages" &&
                    contentWrapper(
                      <LanguagesEdit onCancel={close} onSave={close} />,
                      t("profile:languagesSection.title", { defaultValue: "Languages" }),
                    )}
                  {activeTab === "settings" &&
                    contentWrapper(
                      <SettingsEdit onCancel={close} onSave={close} />,
                      t("profile:settingsSection.edit", { defaultValue: "Settings" }),
                    )}
                </motion.div>
              </AnimatePresence>
              <PersistentFooterActions />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
